/**
 * Utility functions for creating, cloning, and manipulating elements
 */

import { v4 as uuidv4 } from 'uuid';
import { getElementDefinition } from '../elementRegistry';

/**
 * Generate a unique ID for an element
 */
export const generateElementId = (type) => {
  return `${type}-${uuidv4()}`;
};

/**
 * Create a new element from a type definition
 */
export const createElement = (type, overrides = {}) => {
  const definition = getElementDefinition(type);

  if (!definition) {
    throw new Error(`Unknown element type: ${type}`);
  }

  const element = {
    id: generateElementId(type),
    type: type,
    ...overrides
  };

  // Add content or props based on element type
  if (definition.isContainer) {
    // Container elements have props and children
    element.props = { ...definition.defaultProps, ...(overrides.props || {}) };
    element.children = overrides.children || [];
  } else {
    // Content elements have content
    element.content = { ...definition.defaultContent, ...(overrides.content || {}) };
  }

  return element;
};

/**
 * Clone an element (deep copy)
 */
export const cloneElement = (element) => {
  const cloned = {
    ...element,
    id: generateElementId(element.type)
  };

  // Deep clone props/content
  if (element.props) {
    cloned.props = JSON.parse(JSON.stringify(element.props));
  }

  if (element.content) {
    cloned.content = JSON.parse(JSON.stringify(element.content));
  }

  // Deep clone children
  if (element.children) {
    cloned.children = element.children.map(child => cloneElement(child));
  }

  return cloned;
};

/**
 * Create a row with columns from a preset
 */
export const createRowFromPreset = (preset) => {
  const row = createElement('row', {
    props: {
      gap: 'medium',
      alignment: 'stretch',
      reverseOnMobile: false
    }
  });

  // Create columns using 12-column span system
  row.children = preset.columns.map((colDef) => {
    // Support both new 'span' and old 'width' properties for backward compatibility
    const span = colDef.span || convertWidthToSpan(colDef.width) || 12;

    return createElement('column', {
      props: {
        span: span,
        padding: 'medium',
        verticalAlign: 'top'
      }
    });
  });

  return row;
};

/**
 * Convert old width fractions to 12-column spans (for backward compatibility)
 */
const convertWidthToSpan = (width) => {
  const widthMap = {
    'full': 12,
    '1/2': 6,
    '1/3': 4,
    '2/3': 8,
    '1/4': 3,
    '3/4': 9,
    '1/6': 2,
    '5/6': 10,
    'auto': 12
  };

  return widthMap[width];
};

/**
 * Create a grid container with cells
 */
export const createGridWithCells = (rows = 2, columns = 2, props = {}) => {
  const gridContainer = createElement('grid-container', {
    props: {
      rows,
      columns,
      gap: 'medium',
      minRowHeight: '100px',
      ...props
    }
  });

  // Create cells for the grid (rows * columns)
  const totalCells = rows * columns;
  gridContainer.children = [];

  for (let i = 0; i < totalCells; i++) {
    const cell = createElement('grid-cell', {
      props: {
        rowSpan: 1,
        columnSpan: 1,
        padding: 'medium',
        verticalAlign: 'top',
        horizontalAlign: 'left'
      }
    });
    gridContainer.children.push(cell);
  }

  return gridContainer;
};

/**
 * Validate element structure
 */
export const validateElement = (element) => {
  if (!element || !element.id || !element.type) {
    return { valid: false, error: 'Invalid element structure' };
  }

  const definition = getElementDefinition(element.type);

  if (!definition) {
    return { valid: false, error: `Unknown element type: ${element.type}` };
  }

  // Validate required fields in schema
  const schema = definition.schema || {};

  if (definition.isContainer && element.props) {
    for (const [key, fieldSchema] of Object.entries(schema)) {
      if (fieldSchema.required && !element.props[key]) {
        return { valid: false, error: `Missing required field: ${key}` };
      }
    }
  } else if (!definition.isContainer && element.content) {
    for (const [key, fieldSchema] of Object.entries(schema)) {
      if (fieldSchema.required && !element.content[key]) {
        return { valid: false, error: `Missing required field: ${key}` };
      }
    }
  }

  // Validate children if container
  if (definition.isContainer && element.children) {
    for (const child of element.children) {
      const childValidation = validateElement(child);
      if (!childValidation.valid) {
        return childValidation;
      }
    }
  }

  return { valid: true };
};

/**
 * Generate a name attribute from a label
 */
export const generateNameFromLabel = (label) => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * Count total elements in a tree (including nested)
 */
export const countElements = (elements) => {
  let count = 0;

  const traverse = (element) => {
    count++;
    if (element.children) {
      element.children.forEach(traverse);
    }
  };

  elements.forEach(traverse);
  return count;
};

/**
 * Find an element by ID in a tree
 */
export const findElementById = (elements, id) => {
  for (const element of elements) {
    if (element.id === id) {
      return element;
    }

    if (element.children) {
      const found = findElementById(element.children, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Get the path to an element (array of IDs)
 */
export const getElementPath = (elements, targetId, currentPath = []) => {
  for (const element of elements) {
    const path = [...currentPath, element.id];

    if (element.id === targetId) {
      return path;
    }

    if (element.children) {
      const childPath = getElementPath(element.children, targetId, path);
      if (childPath) {
        return childPath;
      }
    }
  }

  return null;
};

/**
 * Get parent element of a target element
 */
export const getParentElement = (elements, targetId) => {
  for (const element of elements) {
    if (element.children) {
      const found = element.children.find(child => child.id === targetId);
      if (found) {
        return element;
      }

      const parent = getParentElement(element.children, targetId);
      if (parent) {
        return parent;
      }
    }
  }

  return null;
};

/**
 * Update an element by ID
 */
export const updateElement = (elements, targetId, updates) => {
  return elements.map(element => {
    if (element.id === targetId) {
      return { ...element, ...updates };
    }

    if (element.children) {
      return {
        ...element,
        children: updateElement(element.children, targetId, updates)
      };
    }

    return element;
  });
};

/**
 * Delete an element by ID
 */
export const deleteElement = (elements, targetId) => {
  return elements.filter(element => {
    if (element.id === targetId) {
      return false;
    }

    if (element.children) {
      return {
        ...element,
        children: deleteElement(element.children, targetId)
      };
    }

    return true;
  }).map(element => {
    if (element.children) {
      return {
        ...element,
        children: deleteElement(element.children, targetId)
      };
    }
    return element;
  });
};

/**
 * Insert element at a specific path
 */
export const insertElementAt = (elements, path, newElement, index) => {
  if (path.length === 0) {
    // Insert at root level
    const newElements = [...elements];
    newElements.splice(index, 0, newElement);
    return newElements;
  }

  const [parentId, ...restPath] = path;

  return elements.map(element => {
    if (element.id === parentId) {
      if (restPath.length === 0) {
        // Insert into this element's children
        const newChildren = [...(element.children || [])];
        newChildren.splice(index, 0, newElement);
        return { ...element, children: newChildren };
      } else {
        // Continue traversing
        return {
          ...element,
          children: insertElementAt(element.children || [], restPath, newElement, index)
        };
      }
    }

    return element;
  });
};

/**
 * Move element from one position to another
 */
export const moveElement = (elements, sourceId, destinationPath, destinationIndex) => {
  // First, find and remove the element
  let elementToMove = null;

  const removeElement = (els) => {
    return els.filter(el => {
      if (el.id === sourceId) {
        elementToMove = el;
        return false;
      }

      if (el.children) {
        el.children = removeElement(el.children);
      }

      return true;
    });
  };

  let newElements = removeElement([...elements]);

  // Then insert it at the new location
  if (elementToMove) {
    newElements = insertElementAt(newElements, destinationPath, elementToMove, destinationIndex);
  }

  return newElements;
};

/**
 * Get all form field elements from a tree
 */
export const extractFormFields = (elements) => {
  const fields = [];

  const traverse = (element) => {
    const definition = getElementDefinition(element.type);

    if (definition && definition.category === 'form') {
      fields.push(element);
    }

    if (element.children) {
      element.children.forEach(traverse);
    }
  };

  elements.forEach(traverse);
  return fields;
};

/**
 * Convert legacy form elements to new format
 */
export const migrateLegacyElement = (legacyElement) => {
  const { type, props, id } = legacyElement;

  // Map old types to new types
  const typeMap = {
    'text': 'text-input',
    'textarea': 'textarea',
    'select': 'select',
    'checkbox': 'checkbox',
    'radio': 'radio',
    'signature': 'signature',
    'heading': 'heading',
    'paragraph': 'paragraph',
    'richtext': 'richtext',
    'divider': 'divider'
  };

  const newType = typeMap[type] || type;
  const definition = getElementDefinition(newType);

  if (!definition) {
    console.warn(`Cannot migrate unknown element type: ${type}`);
    return null;
  }

  const newElement = {
    id: id || generateElementId(newType),
    type: newType
  };

  if (definition.isContainer) {
    newElement.props = { ...definition.defaultProps, ...props };
    newElement.children = [];
  } else {
    newElement.content = { ...definition.defaultContent, ...props };
  }

  return newElement;
};
